import { CountryCode, Order, ProductCodeKey } from '@dayetopia/library';
import axios from 'axios';
import {
  CartItem,
  CartItemGiftType,
  CartItems,
  CartItemType,
  CouponDiscountType,
  CouponState,
  CurrencyObj,
  PromotionObject,
  TFunction
} from '@contracts';
import { getProductTitle } from '@data/products';
import { getFromLocalStorage } from '@utils/localStorage';
import { sentryCaptureException } from '@utils/sentry';
import { isPromotionActive } from './contextHelpers';
import { createFreeGiftWithProductCode } from './freeGift';
import getOmsUrl from './getOmsUrl';
import { trackEvent } from './tracking';

function showInvalidCouponError(t: TFunction, setCouponError: (error: string) => void) {
  setCouponError(t('components.invalid-coupon'));
  setTimeout(() => setCouponError(''), 3000);
}

export async function verifyCouponOnSubscriptionReactivation(
  planCode: string | null | undefined,
  setCouponError: (error: string) => void,
  coupon: string,
  setCheckoutDiscountAmount: (discountAmount: number) => void,
  currency: CurrencyObj,
  price: number,
  t: TFunction
) {
  const currencyCode = currency.letterCode;

  let couponInfo = await getCoupon(coupon);
  if (!couponInfo) {
    couponInfo = await getCoupon(`${coupon}-subscription`);
  }

  if (couponInfo) {
    const { code, state } = couponInfo;

    if (!code || state !== CouponState.Redeemable) {
      showInvalidCouponError(t, setCouponError);
      return;
    }

    if (!couponInfo.appliesToAllPlans) {
      const appliesToPlan = couponInfo.plans?.find((p) => p.code === planCode);
      if (!appliesToPlan) {
        showInvalidCouponError(t, setCouponError);
        return;
      }
    }

    const discount = calculateDiscount(couponInfo, price, currencyCode);
    setCheckoutDiscountAmount(discount);
  } else {
    showInvalidCouponError(t, setCouponError);
  }
}

const trackCouponsError = (code: string, cartItems: CartItems) => {
  if (Array.isArray(cartItems)) {
    const productCodes: ProductCodeKey[] = cartItems.map((item) => item.productCode);
    const productCodesString: string = productCodes.join(', ');
    trackEvent('coupon_redeem_error', { coupon: code, cartItems: productCodesString });
  } else {
    trackEvent('coupon_redeem_error', { coupon: code, cartItems: '' });
  }
};

export async function verifyCouponOnBilling(
  setCouponError: (error: string) => void,
  coupon: string,
  setCheckoutDiscountAmount: (discountAmount: number) => void,
  setCouponObject: (couponObject: any) => void,
  cartItems: CartItems,
  country: CountryCode,
  currency: CurrencyObj,
  price: number,
  t: TFunction,
  handleFreeGift: (cartItem: CartItem) => void,
  orders: Order[],
  location: { pathname?: string } | null | undefined
) {
  try {
    const currencyCode = currency.letterCode;
    const subscriptionCouponCode = `${coupon}-subscription`;
    const shopCouponCode = `${coupon}-shop`;
    const couponInfo = await getCoupon(coupon);

    let couponSub;
    let couponShop;

    if (coupon.startsWith('joindaye-') && orders.length > 0) {
      setCheckoutDiscountAmount(0);
      setCouponError(t('components.coupon-first-order-only'));
      return;
    }

    if (!couponInfo) {
      couponSub = await getCoupon(subscriptionCouponCode);
      couponShop = await getCoupon(shopCouponCode);

      if (!(couponSub && couponShop && couponSub?.state === CouponState.Redeemable && couponShop?.state === CouponState.Redeemable)) {
        trackCouponsError(coupon, cartItems);
        setCheckoutDiscountAmount(0);
        showInvalidCouponError(t, setCouponError);
        return;
      }
    } else {
      const { code, state } = couponInfo;
      if (!code || state !== CouponState.Redeemable) {
        trackCouponsError(coupon, cartItems);
        setCheckoutDiscountAmount(0);
        showInvalidCouponError(t, setCouponError);
        return;
      }
    }

    const isUpdateBillingPage = location?.pathname?.includes('/dashboard/update-billing');
    if (isUpdateBillingPage) {
      if (couponSub) couponSub.code = coupon;
      setCouponObject(couponInfo || couponSub);
      setCheckoutDiscountAmount(1);
      setCouponError('');
      return;
    }

    const promotionsValid = handlePromotions(coupon, cartItems, country, currency, price, setCouponError, handleFreeGift, t);
    if (!promotionsValid) {
      setCheckoutDiscountAmount(0);
      return;
    }

    let discountValid = false;
    let customErrorMessage = '';
    if (couponInfo) {
      const discountCheck = checkDiscountAgainstCartItems(couponInfo, couponInfo, cartItems, currencyCode, setCheckoutDiscountAmount, t);
      discountValid = discountCheck.valid;
      customErrorMessage = discountCheck.message;
    } else {
      const discountCheck = checkDiscountAgainstCartItems(couponSub, couponShop, cartItems, currencyCode, setCheckoutDiscountAmount, t);
      discountValid = discountCheck.valid;
      customErrorMessage = discountCheck.message;
    }

    if (!discountValid) {
      trackCouponsError(coupon, cartItems);
      setCheckoutDiscountAmount(0);
      setCouponError(customErrorMessage);
      return;
    }

    setCouponError('');
    if (couponSub) couponSub.code = coupon;
    setCouponObject(couponInfo || couponSub);
  } catch (err) {
    trackCouponsError(coupon, cartItems);
    setCheckoutDiscountAmount(0);
    showInvalidCouponError(t, setCouponError);
  }
}

function handlePromotions(
  coupon: string,
  cartItems: CartItems,
  country: CountryCode,
  currency: CurrencyObj,
  price: number,
  setCouponError: (error: string) => void,
  handleFreeGift: (cartItem: CartItem) => void,
  t: TFunction
): boolean {
  let promotionsValid = true;
  const promotions: PromotionObject[] = getFromLocalStorage('promotions', true) ?? [];

  const codePromo = promotions.find((promotion) => promotion.couponCode?.trim()?.toLowerCase() === coupon?.trim()?.toLowerCase());
  if (codePromo) {
    const isPromoActive = isPromotionActive(codePromo, country);

    if (!isPromoActive) {
      setCouponError(t('components.coupon-not-valid'));
      promotionsValid = false;
    }

    const requiredProducts = codePromo.productCodes;
    if (requiredProducts?.length && !requiredProducts.some((item) => cartItems?.find((cartItem) => cartItem.productCode === item))) {
      const productCodesString: string = requiredProducts.map((item) => t(getProductTitle(item))).join(',');
      setCouponError(t('components.coupon-requires-products', { products: productCodesString }));
      promotionsValid = false;
    }

    const { spendRequirement } = codePromo;
    if (spendRequirement && spendRequirement[country] && (price ?? 0) < spendRequirement[country]) {
      setCouponError(t('components.coupon-requires-spend', { symbol: currency.symbol, amount: spendRequirement[country] }));
      promotionsValid = false;
    }

    if (promotionsValid) {
      if (codePromo.freeGift) {
        const freeGift = createFreeGiftWithProductCode(codePromo.freeGift as ProductCodeKey, country, currency, CartItemGiftType.PromotionCode);
        handleFreeGift(freeGift);
      }
    } else {
      trackCouponsError(coupon, cartItems);
    }
  }

  return promotionsValid;
}

function calculateDiscount(couponObject: any, totalPrice: number, currencyCode: string) {
  if (totalPrice === 0) {
    return 0;
  }

  const { discount } = couponObject;

  if (discount.type === CouponDiscountType.Percent) {
    const exactValue = totalPrice * (discount.percent / 100); // Ensure correct percentage calculation
    const roundedValue = Math.round((exactValue + Number.EPSILON) * 100) / 100;
    return roundedValue;
  }

  const discountForCurrency = discount.currencies.find((a) => a.currency === currencyCode);

  if (discountForCurrency) {
    const discountTotal = discountForCurrency.amount;
    const exactValue = Math.min(discountTotal, totalPrice); // Ensure discount does not exceed total price
    const roundedValue = Math.round((exactValue + Number.EPSILON) * 100) / 100;
    return roundedValue;
  }

  return 0;
}

function checkDiscountAgainstCartItems(
  couponSub: any,
  couponShop: any,
  cartItems: CartItems,
  currencyCode: string,
  setCheckoutDiscountAmount: (amount: number) => void,
  t: TFunction
): { valid: boolean; message: string } {
  if (!cartItems) return { valid: false, message: t('components.coupon-no-cart-items') };

  const { appliesToAllPlans, plans } = couponSub;
  const { appliesToAllItems, appliesToNonPlanCharges, items } = couponShop;
  let isValid = false;
  let errorMessage = '';
  let subscriptionItemsToDiscount: CartItems = [];
  let oneOffItemsToDiscount: CartItems = [];
  const hasRequiredItems = !!(items && items?.length > 0);
  const hasRequiredPlans = !!(plans && plans?.length > 0);
  const oneOffItems = cartItems.filter((item) => item.type === CartItemType.OneOffPurchase || item.type === CartItemType.Consultation);
  const subscriptionItems = cartItems.filter((item) => item.type === CartItemType.Subscription);

  // Check specific required items first (highest priority)
  if (hasRequiredItems) {
    items.forEach((item) => {
      const cartItemsForCouponItem = oneOffItems.filter((cartItem) => cartItem.productCode === item.code);
      if (cartItemsForCouponItem.length > 0) {
        isValid = true;
        oneOffItemsToDiscount = [...oneOffItemsToDiscount, ...cartItemsForCouponItem];
      }

      oneOffItems.forEach((cartItem) => {
        if (cartItem.boxItems && cartItem.boxItems.length > 0) {
          const boxItemsForCouponItem = cartItem.boxItems.filter((boxItem) => boxItem.name === item.code);
          if (boxItemsForCouponItem.length > 0) {
            isValid = true;
            const discountItem = boxItemsForCouponItem.map((bi) => ({ price: bi.price ?? 0, quantity: bi.count }));
            oneOffItemsToDiscount = [...oneOffItemsToDiscount, ...discountItem];
          }
        }
      });
    });
  }

  // Check specific required plans (highest priority)
  if (hasRequiredPlans) {
    plans.forEach((plan) => {
      const subscriptionCartItems = subscriptionItems.filter((item): item is CartItem & { selectedPlan: any } => 'selectedPlan' in item);
      const cartItemsForCouponItem = subscriptionCartItems.filter((cartItem) => cartItem.selectedPlan.planCode === plan.code);
      if (cartItemsForCouponItem.length > 0) {
        isValid = true;
        subscriptionItemsToDiscount = [...subscriptionItemsToDiscount, ...cartItemsForCouponItem];
      }
    });
  }

  // Only check "applies to all" scenarios if no specific items/plans are defined
  if (!hasRequiredItems && oneOffItems.length > 0 && (appliesToNonPlanCharges || appliesToAllItems)) {
    isValid = true;
    oneOffItemsToDiscount = [...oneOffItemsToDiscount, ...oneOffItems];
  }

  if (!hasRequiredPlans && subscriptionItems.length > 0 && appliesToAllPlans) {
    isValid = true;
    subscriptionItemsToDiscount = [...subscriptionItemsToDiscount, ...subscriptionItems];
  }

  // Set error message only if validation failed
  if (!isValid) {
    if (hasRequiredItems || hasRequiredPlans) {
      // Specific items/plans required but not found in cart
      errorMessage = t('components.coupon-invalid-for-cart');
    } else {
      // Determine what the coupon applies to based on its configuration
      const couponAppliesToOneOffs = appliesToNonPlanCharges || appliesToAllItems || hasRequiredItems;
      const couponAppliesToSubscriptions = appliesToAllPlans || hasRequiredPlans;

      if (couponAppliesToSubscriptions && !couponAppliesToOneOffs && oneOffItems.length > 0) {
        // Coupon is for subscriptions only, but cart has only one-offs
        errorMessage = t('components.coupon-subscription-only');
      } else if (couponAppliesToOneOffs && !couponAppliesToSubscriptions && subscriptionItems.length > 0) {
        // Coupon is for one-offs only, but cart has only subscriptions
        errorMessage = t('components.coupon-oneoff-only');
      } else {
        errorMessage = t('components.coupon-invalid-for-cart');
      }
    }
  }

  if (isValid) {
    let totalCartPrice = 0;
    let subscriptionsDiscount = 0;
    let oneOffsDiscount = 0;

    if (subscriptionItemsToDiscount.length > 0) {
      const subscriptionPrice = subscriptionItemsToDiscount.reduce((total, item) => total + item.price, 0);
      totalCartPrice += subscriptionPrice;
      subscriptionsDiscount = calculateDiscount(couponSub, subscriptionPrice, currencyCode);
    }

    if (oneOffItemsToDiscount.length > 0) {
      const oneOffPrice = oneOffItemsToDiscount.reduce((total, item) => total + item.price * item.quantity, 0);
      totalCartPrice += oneOffPrice;
      oneOffsDiscount = calculateDiscount(couponShop, oneOffPrice, currencyCode);
    }

    const totalDiscountValue = calculateTotalDiscountValue(
      couponSub,
      couponShop,
      subscriptionsDiscount,
      oneOffsDiscount,
      totalCartPrice,
      currencyCode
    );

    // If discount is 0, treat as invalid and show appropriate error
    if (totalDiscountValue === 0) {
      isValid = false;
      // Check coupon configuration to determine what it's intended for
      const subDiscountAmount =
        couponSub?.discount?.type === CouponDiscountType.Percent
          ? couponSub?.discount?.percent || 0
          : couponSub?.discount?.currencies?.find((c) => c.currency === currencyCode)?.amount || 0;
      const shopDiscountAmount =
        couponShop?.discount?.type === CouponDiscountType.Percent
          ? couponShop?.discount?.percent || 0
          : couponShop?.discount?.currencies?.find((c) => c.currency === currencyCode)?.amount || 0;

      const couponHasSubscriptionDiscount = subDiscountAmount > 0;
      const couponHasOneOffDiscount = shopDiscountAmount > 0;

      if (couponHasSubscriptionDiscount && !couponHasOneOffDiscount && oneOffItems.length > 0 && subscriptionItems.length === 0) {
        // Subscription discount configured but cart has only one-offs
        errorMessage = t('components.coupon-subscription-only');
      } else if (couponHasOneOffDiscount && !couponHasSubscriptionDiscount && subscriptionItems.length > 0 && oneOffItems.length === 0) {
        // One-off discount configured but cart has only subscriptions
        errorMessage = t('components.coupon-oneoff-only');
      } else {
        errorMessage = t('components.coupon-invalid-for-cart');
      }
    } else {
      setCheckoutDiscountAmount(totalDiscountValue);
    }
  }

  return { valid: isValid, message: errorMessage ?? null };
}

function calculateTotalDiscountValue(
  couponSub: any,
  couponShop: any,
  subscriptionsDiscount: number,
  oneOffsDiscount: number,
  totalCartPrice: number,
  currencyCode: string
): number {
  let totalDiscountValue = 0;

  if (
    couponSub?.code !== couponShop?.code &&
    couponSub?.discount?.type === CouponDiscountType.Fixed &&
    couponShop?.discount?.type === CouponDiscountType.Fixed
  ) {
    totalDiscountValue = Math.min(Math.max(subscriptionsDiscount, oneOffsDiscount), totalCartPrice);
  } else if (couponSub?.discount?.type === CouponDiscountType.Percent || couponShop?.discount?.type === CouponDiscountType.Percent) {
    totalDiscountValue = calculateDiscount(
      { discount: { type: CouponDiscountType.Percent, percent: Math.max(couponSub?.discount?.percent || 0, couponShop?.discount?.percent || 0) } },
      totalCartPrice,
      currencyCode
    );
  } else {
    totalDiscountValue = Math.min(subscriptionsDiscount + oneOffsDiscount, totalCartPrice);
  }

  // Only apply max discount amount if it's greater than 0
  const maxDiscountAmount = Math.max(
    couponSub?.discount?.currencies?.find((c) => c.currency === currencyCode)?.amount || 0,
    couponShop?.discount?.currencies?.find((c) => c.currency === currencyCode)?.amount || 0
  );

  const finalDiscountValue = maxDiscountAmount > 0 ? Math.min(totalDiscountValue, maxDiscountAmount) : totalDiscountValue;

  return finalDiscountValue;
}

export const getCoupon = async (coupon: string) => {
  const omsUrl = getOmsUrl() ?? '';
  const url = `${omsUrl}/recurly/coupons/${coupon}`;

  try {
    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json; charset=utf-8'
      }
    });
    return response.data;
  } catch (err: any) {
    if (err?.response?.status !== 400) {
      sentryCaptureException('Failed to verify coupon', err, { tags: { coupon } });
    }
    return null;
  }
};
